import {
  Card,
  CardContent,
  FormHelperText,
  Grid,
  InputAdornment,
  makeStyles,
  Switch
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import { grey } from "@material-ui/core/colors";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import { InfoOutlined } from "@material-ui/icons";
import CardTitle from "@saleor/components/CardTitle";
import FormSpacer from "@saleor/components/FormSpacer";
import MultiChipsSelectField from "@saleor/components/MultiChipsSelectField";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { COUPON_COLLECTION_TYPE_CHOICES } from "@saleor/coupons/constants";
import { useValidateCouponCollection } from "@saleor/coupons/hooks/UseValidateCouponCollection";
import { couponFullDetails_coupon } from "@saleor/coupons/types/CouponFullDetails";
import { MallTags_malls_edges_node } from "@saleor/coupons/types/MallTags";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { useMallListQuery } from "@saleor/malls/queries";
import { maybe } from "@saleor/misc";
import { FetchMoreProps } from "@saleor/types";
import {
  CouponAssigneeEnum,
  CouponCategoryEnum,
  CouponCollectionCategoryEnum,
  CouponDealTypeEnum,
  CouponRedeemTypeEnum,
  SmopStatusEnum,
  StaffPromoCreatorEnum
} from "@saleor/types/globalTypes";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType
} from "../../../components/SingleAutocompleteSelectField";
import { CouponCreateFormData } from "../CouponCreatePage";
import { CouponFullDetailsPageFormData } from "../CouponDetailsPage";
import CouponTaggingCheckbox from "../CouponTaggingCard/subcomponents/Checkbox/Checkbox";
import { TooltipArrowLeft, TooltipArrowUp } from "./Tooltip";

export interface CouponDetailsProps {
  assigneeChoices: Array<{ label: string; value: CouponAssigneeEnum }>;
  coupon?: couponFullDetails_coupon;
  onAdditionalCopies?: (value: number) => void;
  additionalCopies?: number;
  disabled: boolean;
  hostMallDisplay?: string;
  data: CouponCreateFormData | CouponFullDetailsPageFormData;
  fetchMalls?: (query: string) => void;
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreCollection?: FetchMoreProps;
  fetchCollections?: (query: string) => void;
  selectedCategory: string[];
  setSelectedCategory: React.Dispatch<React.SetStateAction<any[]>>;
  couponCollections: SingleAutocompleteChoiceType[];
  couponCollectionDisplayValue: string;
  malls: MallTags_malls_edges_node[] | SingleAutocompleteChoiceType[];
  hostMall: SingleAutocompleteChoiceType[];
  isFeatured?: boolean;
  onChange: FormChange;
  error: {
    assignee: null;
    mainPromoTitle: null;
    redeemType: null;
    couponCode: null;
    couponValue: null;
    eventLocation: null;
    totalCopies: null;
    category: null;
  };
  onMallChange(event: React.ChangeEvent<any>);
  onCouponCollectionChange(event: React.ChangeEvent<any>);
  onCouponCategoryChange(event: React.ChangeEvent<any>);
  onCouponClassificationChange(event: React.ChangeEvent<any>);
}

const useStyles = makeStyles(
  theme => ({
    card: {
      overflow: "visible"
    },
    cardTitle: {
      "& span": {
        fontWeight: "bold"
      }
    },
    dashedBorder: {
      backgroundSize: "100px 10px",
      border: "2px dashed #E0E4EA",
      width: "100%"
    },
    gridItemAlignment: {
      alignItems: "center",
      display: "flex"
    },
    helperText: {
      "& span": {
        color: grey[800]
      },
      color: "#EC5D77",
      margin: "2px 0px"
    },
    informationIcon: {
      color: "#247CFF"
    },
    textFieldGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    }
  }),
  { name: "CouponDetails" }
);

const allMallTag = [
  {
    label: "All malls",
    value: null
  }
];

const useCouponMalls = (malls, onChange, data) => {
  const notify = useNotifier();
  const [allMallList, updateAllMallList] = useState([]);
  const [stopQuery, setStopQuery] = useState(false);
  const [mallQueryAfterPagination, setMallQueryAfterPagination] = useState("");
  const [mallDisplayValue, setMallDisplayValue] = useState("");

  const { user } = useUser();

  const isUserTypeNull = useMemo(
    () => user?.permissionGroups.some(group => group.smopUsertypeCode === null),
    [user]
  );

  const userMallIds = React.useMemo(
    () => new Set(user.malls.edges.map(edge => edge.node.id)),
    [user]
  );

  const {
    data: mallQuery,
    loading: mallListLoading,
    refetch: refetchMallList
  } = useMallListQuery({
    skip: stopQuery,
    variables: {
      after: mallQueryAfterPagination,
      filter: {
        status: SmopStatusEnum.ACT
      },
      first: 100
    }
  });

  const mallNextPageQuery = mallQuery?.malls?.pageInfo;
  useEffect(() => {
    if (mallQuery && !mallListLoading) {
      updateAllMallList(current => [
        ...current,
        ...mallQuery.malls.edges.map(edge => ({
          label: edge.node.name,
          value: edge.node.id
        }))
      ]);
      setMallQueryAfterPagination(mallNextPageQuery.endCursor);
      if (
        !mallNextPageQuery.hasNextPage &&
        mallNextPageQuery?.endCursor === null
      ) {
        setMallQueryAfterPagination("");
        setStopQuery(true);
      } else if (mallQuery?.malls.pageInfo?.endCursor) {
        refetchMallList();
      }
    }
  }, [mallQuery]);

  const choices = maybe(
    () =>
      malls.map(mall => ({
        label: mall.name,
        value: mall.id
      })),
    []
  );

  const handleMallsChange = event => {
    const { name, value } = event.target;
    if (data.malls.includes(value)) {
      handleMallsRemove(event);
    } else {
      onChange({
        target: {
          name,
          value: [...data.malls, value]
        }
      });
    }
  };

  const handleMallsRemove = event => {
    const { name, value } = event.target;
    if (value === null) {
      notify({
        status: "error",
        text: "Participating malls cannot be blank",
        title: "Coupon Form Error"
      });
      return;
    }
    if (isUserTypeNull && data.malls.length === 1 && userMallIds.size !== 0) {
      notify({
        status: "error",
        text:
          "You have to assign at least one of the assigned mall in your account",
        title: "Coupon Form Error"
      });
      return;
    }
    onChange({
      target: {
        name,
        value: data.malls.filter(mall => mall !== value)
      }
    });
  };

  const getProcessedMalls = (data, allMallList) => {
    if (
      data.couponClassification === "DEALS" &&
      (!data.malls || data.malls.length === 0 || data.malls[0] === null)
    ) {
      return allMallTag;
    } else if (data.couponClassification === "EVENTS") {
      const foundMall = allMallList.find(mall => mall.value === data.malls[0]);
      return foundMall
        ? [{ label: foundMall.label, value: foundMall.value }]
        : [];
    } else {
      return data.malls
        .map(selectedMallId => {
          const foundMall = allMallList.find(
            mall => mall.value === selectedMallId
          );
          return foundMall
            ? { label: foundMall.label, value: foundMall.value }
            : null;
        })
        .filter(mall => mall !== null);
    }
  };

  const handleMallChange = event => {
    const mallId = event.target.value;
    const foundMall = allMallList.find(mall => mall.value === mallId);

    const newMallDisplayValue = foundMall ? foundMall.label : "";

    setMallDisplayValue(newMallDisplayValue);

    const updatedMalls =
      data.couponClassification === "EVENTS"
        ? [mallId]
        : [...data.malls, mallId];

    onChange({
      target: {
        name: "malls",
        value: updatedMalls
      }
    });
  };

  const processedMalls = getProcessedMalls(data, allMallList);

  const transformedMallChoices = Array.isArray(choices)
    ? choices.filter(choice => !data.malls.includes(choice.value))
    : [];

  return [
    handleMallChange,
    mallDisplayValue,
    handleMallsChange,
    handleMallsRemove,
    processedMalls,
    transformedMallChoices
  ];
};

export const CouponDetails: React.FC<CouponDetailsProps> = props => {
  const {
    assigneeChoices,
    malls,
    disabled,
    onChange,
    error,
    data,
    coupon,
    onAdditionalCopies,
    additionalCopies,
    fetchMalls,
    fetchMoreMalls,
    fetchMoreCollection,
    fetchCollections,
    setSelectedCategory,
    couponCollections,
    couponCollectionDisplayValue,
    onCouponCollectionChange,
    onCouponCategoryChange,
    onCouponClassificationChange,
    selectedCategory
  } = props;
  const intl = useIntl();
  const classes = useStyles({});
  const showAdditionalCopiesTxt =
    coupon?.status !== undefined && coupon?.status !== "DRAFT";
  const enableCouponValue =
    coupon?.status !== undefined &&
    (coupon?.status === "DRAFT" || coupon?.status === "INACTIVE");
  const isMallEvents = data.couponClassification === CouponDealTypeEnum.EVENTS;

  const { user } = useUser();

  const isUserTypeNull = user?.permissionGroups.some(
    group => group.smopUsertypeCode === null
  );

  const userPromoCreator = user?.promoCreator;

  const redeemTypeChoices = [
    {
      label: "Claim Now, Redeem Later",
      value: CouponRedeemTypeEnum.REDEEM_LATER
    },
    { label: "Redeem Now", value: CouponRedeemTypeEnum.REDEEM_NOW }
  ];

  const couponClassificationChoices = [
    {
      label: "Mall Deals",
      value: CouponDealTypeEnum.DEALS
    },
    {
      label: "Mall Events",
      value: CouponDealTypeEnum.EVENTS
    }
  ];

  const couponCategoryChoices = isMallEvents
    ? [{ label: "Events", value: "" }]
    : [
        { label: "Dining", value: CouponCategoryEnum.DINING },
        { label: "Cyberzone", value: CouponCategoryEnum.CYBERZONE },
        { label: "Wellness", value: CouponCategoryEnum.WELLNESS },
        {
          label: "Entertainment",
          value: CouponCategoryEnum.ENTERTAINMENT
        },
        { label: "Shopping", value: CouponCategoryEnum.SHOPPING },
        { label: "Groceries", value: CouponCategoryEnum.GROCERIES }
      ];

  const [isActiveParticipatingMalls, setIsActiveParticipatingMalls] = useState(
    false
  );

  const [, setTagMallText] = useState("");

  const [
    isExclusiveRedemptionVisible,
    setIsExclusiveRedemptionVisible
  ] = useState(false);

  const [
    isCollectionValidationPending,
    setIsCollectionValidationPending
  ] = useState(false);

  const [
    handleMallChange,
    mallDisplayValue,
    handleMallsChange,
    handleMallsRemove,
    processedMalls,
    transformedMallChoices,
    selectedMalls
  ] = useCouponMalls(malls, onChange, data);

  const handleCouponValueChange = e => {
    if (/^[.0-9\b]+$/.test(e.target.value) || e.target.value === "") {
      onChange(e);
    }
  };

  const handleTotalCopiesChange = e => {
    if (/^[0-9]+$/.test(e.target.value) || e.target.value === "") {
      onChange(e);
    }
  };

  const handleCouponValueInput = e => {
    if (["-", "+", "e"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleTotalCopiesInput = e => {
    if ([".", "-", "+", "e"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleAdditionalCopiesInput = e => {
    if (/^[0-9]+$/.test(e.target.value) || e.target.value === "") {
      onAdditionalCopies(e.target.value);
    }
  };

  const handleKeyPress = e => {
    if (/[`[/!@#$%^&*()_+\=\]{};:"\\|,.<>?~]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleIsNationwideChange = event => {
    const isChecked = event.target.checked;
    onChange({
      target: {
        name: "isNationwide",
        value: isChecked
      }
    });
  };

  const isInactiveEditable = coupon?.status === "INACTIVE" || !disabled;

  const handleHasMallsChange = () => {
    const updatedHasMalls = !data.hasMalls;
    onChange({
      target: {
        name: "hasMalls",
        value: updatedHasMalls
      }
    });
    setIsActiveParticipatingMalls(!updatedHasMalls);
  };

  const couponCollectionIdForValidation =
    typeof data.couponCollection === "string" && data.couponCollection
      ? data.couponCollection
      : null;

  const {
    collectionType,
    errorCollection,
    validationLoading
  } = useValidateCouponCollection({
    id: couponCollectionIdForValidation
  });

  const handleCouponCollectionSelect = (event: React.ChangeEvent<any>) => {
    if (isCollectionDisabled) {
      return;
    }

    const selectedCollectionId = event?.target?.value || null;

    setIsCollectionValidationPending(!!selectedCollectionId);
    onCouponCollectionChange(event);
  };

  useEffect(() => {
    if (!validationLoading) {
      setIsCollectionValidationPending(false);
    }

    const exclusiveEligible =
      Boolean(couponCollectionIdForValidation) &&
      !validationLoading &&
      !errorCollection &&
      collectionType === COUPON_COLLECTION_TYPE_CHOICES[0].value;

    setIsExclusiveRedemptionVisible(exclusiveEligible);

    const communityType = COUPON_COLLECTION_TYPE_CHOICES[0].value;
    const shouldClearExclusive =
      !couponCollectionIdForValidation ||
      !!errorCollection ||
      (!validationLoading &&
        collectionType != null &&
        collectionType !== communityType);

    if (shouldClearExclusive && data.isExclusiveRedemption) {
      onChange({
        target: {
          name: "isExclusiveRedemption",
          value: false
        }
      });
    }
  }, [
    collectionType,
    couponCollectionIdForValidation,
    data.isExclusiveRedemption,
    errorCollection,
    onChange,
    validationLoading
  ]);

  useEffect(() => {
    const shouldAutoEnableNationwide = !data.hasMalls;

    if (shouldAutoEnableNationwide) {
      if (!data.isNationwide) {
        onChange({
          target: {
            name: "isNationwide",
            value: true
          }
        });
      }
    }
  }, [data.hasMalls]);

  useEffect(() => {
    if (isMallEvents) {
      setSelectedCategory([CouponCollectionCategoryEnum.EVENTS]);
    } else {
      setSelectedCategory([data.category]);
    }
  }, [data.category, data.couponClassification]);

  const selectedSingleMallId = data.malls[0];
  const hostMallSingleDisplay = coupon?.malls?.edges?.find(
    edge => edge.node.id === selectedSingleMallId
  )?.node.name;

  const isCollectionDisabled =
    selectedCategory[0] === null ||
    !["INACTIVE", "DRAFT", undefined].includes(coupon?.status) ||
    validationLoading;

  return (
    <Card>
      <CardTitle
        className={classes.cardTitle}
        title={intl.formatMessage({
          defaultMessage: "Coupon Details",
          description: "section header"
        })}
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SingleSelectField
              name="assignee"
              label={intl.formatMessage({
                defaultMessage: "Coupon Assignee"
              })}
              disabled={
                userPromoCreator === StaffPromoCreatorEnum.MALL_MARKETING ||
                userPromoCreator === StaffPromoCreatorEnum.APP_DEVELOPMENT ||
                coupon?.createdBy.promoCreator ===
                  StaffPromoCreatorEnum.MALL_MARKETING ||
                coupon?.createdBy.promoCreator ===
                  StaffPromoCreatorEnum.APP_DEVELOPMENT ||
                !(
                  coupon?.status === "INACTIVE" ||
                  coupon?.status === "DRAFT" ||
                  coupon?.status === undefined
                )
              }
              choices={assigneeChoices}
              value={data.assignee}
              onChange={onChange}
              error={error.assignee}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TooltipArrowUp
              title="Select if Coupon is for Deals or Events."
              placement="bottom"
            >
              <div>
                <SingleSelectField
                  name="couponClassification"
                  disabled={
                    !(
                      coupon?.status === "INACTIVE" ||
                      coupon?.status === "DRAFT" ||
                      coupon?.status === undefined
                    )
                  }
                  choices={couponClassificationChoices}
                  onChange={onCouponClassificationChange}
                  label={intl.formatMessage({
                    defaultMessage: "Coupon Classification"
                  })}
                  value={data.couponClassification}
                />
              </div>
            </TooltipArrowUp>
          </Grid>

          <FormSpacer />
          <Grid item xs={12} sm={6}>
            <TextField
              label={intl.formatMessage({
                defaultMessage: "Coupon Code",
                description: "Coupon Code"
              })}
              name="couponCode"
              fullWidth
              inputProps={{
                maxLength: 20
              }}
              id="couponCodeTxt"
              disabled={disabled}
              onChange={onChange}
              value={data.couponCode}
              error={!!error.couponCode}
              FormHelperTextProps={{
                className: classes.helperText
              }}
              helperText={
                <>
                  <span>
                    Characters Remaining: {20 - data.couponCode.length}
                  </span>
                  <br />
                  {error.couponCode ? "This field is required." : ""}
                </>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TooltipArrowUp
              title={
                isMallEvents
                  ? "Type “1” if the event is FREE, otherwise indicate amount if there is minimum spend."
                  : ""
              }
              placement="bottom"
            >
              <div>
                <TextField
                  id="couponValueTxt"
                  label={intl.formatMessage({
                    defaultMessage: "Discounted Price",
                    description: "Discounted Price"
                  })}
                  name="couponValue"
                  onChange={handleCouponValueChange}
                  onKeyDown={handleCouponValueInput}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">₱</InputAdornment>
                    )
                  }}
                  type="number"
                  disabled={coupon?.status ? !enableCouponValue : disabled}
                  error={!!error.couponValue}
                  FormHelperTextProps={{
                    className: classes.helperText
                  }}
                  helperText={
                    error.couponValue ? "This field is required." : ""
                  }
                  value={data.couponValue}
                />
              </div>
            </TooltipArrowUp>
          </Grid>
          <FormSpacer />
          <Grid
            item
            xs={12}
            sm={6}
            className={classNames({
              [classes.textFieldGrid]: showAdditionalCopiesTxt
            })}
          >
            <TooltipArrowUp
              title={
                isMallEvents
                  ? "Should indicate the total number of slots allowed or total event capacity."
                  : ""
              }
              placement="bottom"
            >
              <div>
                <TextField
                  id="totalCopiesTxt"
                  label={intl.formatMessage({
                    defaultMessage: "Max No. of Redemptions",
                    description: "Max No. of Redemptions"
                  })}
                  onChange={handleTotalCopiesChange}
                  onKeyDown={handleTotalCopiesInput}
                  name="totalCopies"
                  value={data.totalCopies}
                  disabled={disabled}
                  fullWidth
                  type="number"
                  error={!!error.totalCopies}
                  FormHelperTextProps={{
                    className: classes.helperText
                  }}
                  InputLabelProps={{
                    shrink: data.totalCopies != null || "" ? true : false
                  }}
                  helperText={
                    error.totalCopies ? "This field is required." : ""
                  }
                />
              </div>
            </TooltipArrowUp>
            {showAdditionalCopiesTxt && (
              <TextField
                id="additionalCopiesTxt"
                label={intl.formatMessage({
                  defaultMessage: "Additional Copies",
                  description: "Additional Copies"
                })}
                onChange={handleAdditionalCopiesInput}
                onKeyDown={handleTotalCopiesInput}
                value={additionalCopies}
                disabled={coupon?.status !== "INACTIVE"}
                fullWidth
                type="number"
                FormHelperTextProps={{
                  className: classes.helperText
                }}
                InputLabelProps={{
                  shrink: data.totalCopies != null || "" ? true : false
                }}
              />
            )}
          </Grid>
          <FormSpacer />
          <Grid item xs={12} sm={6}>
            <SingleSelectField
              name="redeemType"
              disabled={true}
              choices={redeemTypeChoices}
              onChange={onChange}
              label={intl.formatMessage({
                defaultMessage: "Redeem Type"
              })}
              value={redeemTypeChoices[0].value}
              error={!!error.redeemType}
            />
          </Grid>
          <FormSpacer />
          <Grid item xs={12} sm={6}>
            {data.couponClassification === CouponDealTypeEnum.EVENTS ? (
              <TextField
                label={intl.formatMessage({
                  defaultMessage: "Coupon Category"
                })}
                value={"Events"}
                disabled={true}
                fullWidth
              />
            ) : (
              <TooltipArrowUp
                title="Identify the correct category of the product or coupon uploaded."
                placement="bottom"
              >
                <div>
                  <SingleSelectField
                    name="category"
                    disabled={
                      !(
                        coupon?.status === "INACTIVE" ||
                        coupon?.status === "DRAFT" ||
                        coupon?.status === undefined
                      )
                    }
                    choices={couponCategoryChoices}
                    onChange={onCouponCategoryChange}
                    label={intl.formatMessage({
                      defaultMessage: "Coupon Category"
                    })}
                    value={data.category}
                    error={!!error.category}
                  />
                </div>
              </TooltipArrowUp>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <SingleAutocompleteSelectField
              name="couponCollection"
              label={intl.formatMessage({
                defaultMessage: "Coupon Collection"
              })}
              choices={couponCollections}
              disabled={isCollectionDisabled}
              value={data.couponCollection}
              onChange={handleCouponCollectionSelect}
              displayValue={couponCollectionDisplayValue}
              fetchChoices={fetchCollections}
              clearOption={!isCollectionDisabled}
              isFromCoupons={true}
              {...fetchMoreCollection}
              loading={fetchMoreCollection.loading || validationLoading}
            />
            {isExclusiveRedemptionVisible &&
              collectionType !== COUPON_COLLECTION_TYPE_CHOICES[1].value && (
                <CouponTaggingCheckbox
                  isChecked={data.isExclusiveRedemption}
                  onChange={value =>
                    onChange({
                      target: {
                        name: "isExclusiveRedemption",
                        value
                      }
                    })
                  }
                  labelText={intl.formatMessage({
                    defaultMessage: "Exclusive Redemption"
                  })}
                  infoTooltipText="This coupon can only be redeemed in the coupon collection"
                  isDisabled={
                    validationLoading ||
                    isCollectionValidationPending ||
                    isCollectionDisabled
                  }
                  tooltipPlacement="right"
                />
              )}
          </Grid>
          <Grid item xs={12} sm={12}>
            <div className={classes.dashedBorder}></div>
          </Grid>
          <FormSpacer />
          <Grid item xs={12} sm={12}>
            {data.couponClassification === CouponDealTypeEnum.DEALS && (
              <Grid item xs={12}>
                <MultiChipsSelectField
                  name="malls"
                  label={intl.formatMessage({
                    defaultMessage: "Mall"
                  })}
                  choices={transformedMallChoices}
                  fetchChoices={fetchMalls}
                  onFetchMore={fetchMoreMalls.onFetchMore}
                  onKeyPress={handleKeyPress}
                  hasMore={fetchMoreMalls.hasMore}
                  loading={fetchMoreMalls.loading}
                  value={isUserTypeNull ? selectedMalls : processedMalls}
                  onChange={isInactiveEditable ? handleMallsChange : undefined}
                  onRemove={isInactiveEditable ? handleMallsRemove : undefined}
                  onChangeText={setTagMallText}
                  displayValues={processedMalls}
                  disabled={
                    coupon?.status === "INACTIVE" && data.hasMalls === true
                      ? false
                      : disabled || isActiveParticipatingMalls
                  }
                />
                <FormHelperText>
                  <InfoOutlined
                    fontSize="small"
                    style={{
                      position: "relative",
                      top: 5
                    }}
                  />{" "}
                  Malls selected will be tagged as participating malls. If there
                  are no selected malls it will apply to all malls.
                </FormHelperText>
              </Grid>
            )}
            {data.couponClassification === CouponDealTypeEnum.EVENTS && (
              <Grid item xs={12}>
                <TooltipArrowUp
                  title={
                    isMallEvents
                      ? "Select the Mall where the event will be held. For multiple malls, please create multiple coupons."
                      : ""
                  }
                  placement="bottom"
                >
                  <div>
                    <SingleAutocompleteSelectField
                      name="malls"
                      idInputTxt="hostMallTxt"
                      label={intl.formatMessage({
                        defaultMessage: "Host Mall"
                      })}
                      displayValue={mallDisplayValue || hostMallSingleDisplay}
                      onChange={handleMallChange}
                      choices={transformedMallChoices}
                      fetchChoices={fetchMalls}
                      value={selectedSingleMallId}
                      disabled={
                        coupon?.status === "INACTIVE" && data.hasMalls === true
                          ? false
                          : disabled || isActiveParticipatingMalls
                      }
                      {...fetchMoreMalls}
                    />
                  </div>
                </TooltipArrowUp>
                <FormSpacer />
                <TooltipArrowUp
                  title={
                    isMallEvents ? "Type the location within the mall." : ""
                  }
                  placement="bottom"
                >
                  <div>
                    <TextField
                      label={intl.formatMessage({
                        defaultMessage: "Event Place",
                        description: "Event Place for coupons"
                      })}
                      value={data.eventLocation}
                      onChange={onChange}
                      name="eventLocation"
                      type="text"
                      fullWidth
                      disabled={
                        !(
                          coupon?.status === "INACTIVE" ||
                          coupon?.status === "DRAFT" ||
                          coupon?.status === undefined
                        )
                      }
                      error={!!error.eventLocation}
                      helperText={
                        error.eventLocation ? "This field is required" : ""
                      }
                    />
                  </div>
                </TooltipArrowUp>
              </Grid>
            )}
            <FormSpacer />
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              lg={3}
              className={classes.gridItemAlignment}
            >
              {data.couponClassification === CouponDealTypeEnum.DEALS ? (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="isParticipatingMallsCheckbox"
                        name="participatingMalls"
                        checked={!data.hasMalls}
                        onChange={handleHasMallsChange}
                        disabled={
                          !(
                            coupon?.status === "INACTIVE" ||
                            coupon?.status === "DRAFT" ||
                            coupon?.status === undefined
                          )
                        }
                      />
                    }
                    label="No Participating Malls"
                    labelPlacement="end"
                  />
                  <TooltipArrowLeft
                    title="Check this if the coupon you’re creating doesn’t require to go in-store to redeem."
                    placement="right"
                  >
                    <InfoOutlined color="primary" />
                  </TooltipArrowLeft>
                </>
              ) : (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="hasMallsCheckbox"
                        name="hasMalls"
                        checked={!data.hasMalls}
                        onChange={handleHasMallsChange}
                        disabled={
                          !(
                            coupon?.status === "INACTIVE" ||
                            coupon?.status === "DRAFT" ||
                            coupon?.status === undefined
                          )
                        }
                      />
                    }
                    label="No Host Mall"
                    labelPlacement="end"
                  />
                  <TooltipArrowLeft
                    title="Check this if the event coupon you’re creating is outside the SM Mall’s location (i.e Universities or Schools)"
                    placement="right"
                  >
                    <InfoOutlined color="primary" />
                  </TooltipArrowLeft>
                </>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              lg={3}
              className={classes.gridItemAlignment}
            >
              <FormControlLabel
                control={
                  <Switch
                    id="isNationwideToggle"
                    color="primary"
                    name="isNationwide"
                    checked={data.isNationwide}
                    onChange={handleIsNationwideChange}
                    disabled={
                      !(
                        coupon?.status === "INACTIVE" ||
                        coupon?.status === "DRAFT" ||
                        coupon?.status === undefined
                      )
                    }
                  />
                }
                label="Available Nationwide"
                labelPlacement="end"
              />
              <TooltipArrowLeft
                title="Toggle this for your coupon to be viewable nationwide."
                placement="right"
              >
                <InfoOutlined color="primary" />
              </TooltipArrowLeft>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CouponDetails;
